import React from "react";

import Bazaar, { BazaarPlaceholder } from "@/mk2/apps/home/components/Bazaar";
import HomeBaseBannerComponent from "@/mk2/apps/home/components/HomeBaseBannerComponent";
import Post, { PostPlaceholder } from "@/mk2/apps/home/components/Post";
import { Props } from "@/mk2/apps/home/containers/Home/HomePage";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Panel } from "@/mk2/components/Panel";
import { TrackClick } from "@/mk2/components/TrackClick";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState, isLoading } from "@/mk2/reducers/loadingState";
import { HOME_SHOW_MORE_POSTS } from "@/mk/autogenerated/translation";

import styles from "./Home.mscss";

declare var window: any;

function gtag(..._args: any[]): void {
    window.dataLayer.push(arguments);
}

// Flush seen posts every 5 seconds
const FLUSH_INTERVAL_MS = 5000;

class Home extends React.PureComponent<Props> {
    private observer: IntersectionObserver | null = null;
    private flushInterval: number | undefined;
    private pendingSeenPosts = new Set<string>();
    private postRefs = new Map<string, HTMLElement>();

    public componentDidMount() {
        if (typeof window !== "undefined") {
            window.dataLayer = window.dataLayer || [];
            window.gtag = window.gtag || gtag;
            const { homepageAB } = this.props;
            if (homepageAB) {
                gtag("set", "user_properties", {
                    homepage_ab: homepageAB,
                });
            }
        }

        // Only enable seen posts tracking on /hp-test route
        const isHpTest =
            typeof window !== "undefined" &&
            (window.location.pathname === "/hp-test" || window.location.pathname === "/hp-test/");

        if (isHpTest && typeof IntersectionObserver !== "undefined") {
            this.observer = new IntersectionObserver(this.handleIntersections, {
                threshold: 0.5,
            });
            this.flushInterval = window.setInterval(this.flushSeenPosts, FLUSH_INTERVAL_MS);
        }
    }

    public componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
        }
        // Flush any remaining seen posts
        this.flushSeenPosts();
        this.pendingSeenPosts.clear();
    }

    public render() {
        const { loadingState, homePosts } = this.props;

        return (
            <div className={styles.Home}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderInitSuccess}
                    onRenderInit={this.renderInitSuccess}
                    hasData={homePosts && homePosts.length > 0}
                />
            </div>
        );
    }

    private handleIntersections = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const postId = (entry.target as HTMLElement).dataset.postId;
                if (postId) {
                    this.pendingSeenPosts.add(postId);
                }
            }
        });
    };

    private flushSeenPosts = () => {
        const { onCheckout } = this.props;
        if (this.pendingSeenPosts.size === 0) return;
        onCheckout(Array.from(this.pendingSeenPosts));
        this.pendingSeenPosts.clear();
    };

    private observePost = (element: HTMLElement | null, postId: string) => {
        if (!this.observer) return;

        const prevElement = this.postRefs.get(postId);
        if (prevElement && prevElement !== element) {
            this.observer.unobserve(prevElement);
        }

        if (element) {
            this.postRefs.set(postId, element);
            this.observer.observe(element);
        } else {
            this.postRefs.delete(postId);
        }
    };

    private renderInitSuccess = () => {
        const {
            isMobile,
            loadingState,
            nextPage,
            pageLoadingState,
            requestUser,
            history,
            bazaarCounts,
            place,
        } = this.props;

        return (
            <>
                {isLoading(loadingState) ? (
                    <BazaarPlaceholder
                        className={styles.Home__bazaar}
                        isMobile={isMobile}
                        history={history}
                    />
                ) : (
                    <Bazaar
                        className={styles.Home__bazaar}
                        history={history}
                        isMobile={isMobile}
                        prams={bazaarCounts.prams}
                        footwear={bazaarCounts.footwear}
                        toys={bazaarCounts.toys}
                        others={bazaarCounts.others}
                        places={bazaarCounts.places}
                        place={place}
                        requestUser={requestUser}
                    />
                )}

                {isMobile && (
                    <HomeBaseBannerComponent
                        className={styles.Home__baseLink}
                        requestUser={requestUser}
                    />
                )}

                <Panel className={styles.Home__posts}>
                    <>{this.renderPosts()}</>
                    {pageLoadingState === LoadingState.LOADING && (
                        <>
                            <PostPlaceholder className={styles.Home__post} />
                            <PostPlaceholder className={styles.Home__post} />
                            <PostPlaceholder className={styles.Home__post} />
                        </>
                    )}
                    <div className={styles.Home__pager}>
                        {pageLoadingState !== LoadingState.LOADING && nextPage ? (
                            <TrackClick
                                name="homepage_feed_click"
                                props={{ url: "load_more", position: 0, page: nextPage }}
                            >
                                <Btn
                                    type={BtnType.BlueOutline}
                                    label={HOME_SHOW_MORE_POSTS}
                                    onClick={this.loadPage}
                                    data-cy="home-posts-load-more"
                                />
                            </TrackClick>
                        ) : (
                            // Needed for spacing in the flex
                            <div />
                        )}
                    </div>
                </Panel>
            </>
        );
    };

    private renderPosts = () => {
        const { homePosts, loadingState, isMobile } = this.props;
        return isLoading(loadingState)
            ? Array.from(Array(isMobile ? 6 : 30).keys()).map((index) => (
                  <PostPlaceholder key={index} className={styles.Home__post} />
              ))
            : homePosts.map((post, index) => (
                  <div
                      key={post.id}
                      data-post-id={post.hpid || undefined}
                      ref={post.hpid ? (el) => this.observePost(el, post.hpid) : undefined}
                  >
                      <TrackClick
                          name="homepage_feed_click"
                          props={{
                              id: post.id,
                              url: post.url,
                              position: index + 1,
                          }}
                      >
                          <Post className={styles.Home__post} post={post} />
                      </TrackClick>
                  </div>
              ));
    };

    private load = () => {
        const { onLoad, location } = this.props;
        onLoad(location);
    };

    private loadPage = () => {
        if (this.props.nextPage && this.props.pageLoadingState !== LoadingState.LOADING) {
            this.props.onLoadPage(this.props.nextPage);
        }
    };
}

export default Home;
